import { styled, css, useTheme } from '@vebox/etui-system';
import { mediaMax } from '../utilities/responsive.js';
import '../utilities/format-ms.js';
import '../utilities/create-transition.js';
import { useState, createElement } from 'react';
import '../utilities/get-styleprop.js';
import { convertFormatToDateFns } from '../utilities/convertFormatToDateFns.js';
import '../useDebounce/useDebounce.js';
import '../useWindowSize/useWindowSize.js';
import useResponsive from '../useResponsive/useResponsive.js';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div, Ul, Li } from '@vebox/etui-system-elements';
import '../Svg-0180d7b5.js';
import Icon from '../Icon/Icon.js';
import '@vebox/etui-icons';
import { icons } from '../Icon/icons.js';
import '../Icon/helper.js';
import '@vebox/etui-colors';
import '../ErrorLabel/ErrorLabel.js';
import '../FormItem/FormItem.js';
import '../Input/Input.js';
import '../InputLabel/InputLabel.js';
import TextInput from '../TextInput/TextInput.js';
import InputAddon from '../InputAddon/InputAddon.js';
import ClickOutside from '../ClickOutside/ClickOutside.js';
import '../Tooltip/Tooltip.js';
import Button from '../Button/Button.js';
import '../Container/Row.js';
import '../Container/config.js';
import '../Container/Col.js';
import { addToRange } from 'react-day-picker';
import DatePicker from '../DatePicker/DatePicker.js';
import '../DatePicker/NavBar.js';
import { isSameDay, format, isAfter, subDays, subMonths, subYears } from 'date-fns';
import '../DatePicker/formatters.js';
import { H4 } from '../H4/H4.js';
import 'react-modal';
import '../Modal-5d30452a.js';
import '../Modal/ModalHeader.js';
import Modal from '../Modal/Modal.js';

const _excluded = ["theme", "alignRight"];
const FiltersContainerDiv = styled(Div).attrs(_ref => {
  let {
      theme,
      alignRight
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    bg: theme.palette.common.white,
    p: theme.spacing.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'absolute',
    left: `${alignRight ? 'auto' : 0}`,
    right: `${alignRight ? '0' : 'auto'}`,
    top: `calc(${theme.shape.inputHeight}px + -1px)`,
    border: `1px solid ${theme.palette.grey[60]}`,
    borderTop: `1px solid ${theme.palette.grey[40]}`,
    boxShadow: `0 7px 10px -5px rgba(0, 0, 0, 0.3)`,
    zIndex: 101
  }, props);
})``;

const FiltersUl = styled(Ul)`
  list-style: none;
  padding: 0;
  margin-right: ${({
  theme
}) => theme.spacing.md}px;
  margin-top: 0;
  margin-bottom: 0;
  width: 100%;
  text-align: left;

  ${({
  theme
}) => mediaMax(theme).xs`
    margin-right: 0;
    margin-bottom: ${theme.spacing.md}px;
  `};
`;

const FiltersLi = styled(Li)`
  ${({
  selected,
  theme
}) => css`
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.fontSizeBase};
    font-weight: ${theme.typography.fontWeightRegular};
    line-height: ${theme.typography.lineHeightBase};
    border-top: 1px solid ${theme.palette.divider};
    padding: ${theme.spacing.xs}px ${theme.spacing.xs}px ${theme.spacing.xs}px;
    cursor: pointer;
    white-space: nowrap;
    color: ${selected ? theme.palette.action.active : theme.palette.text.primary};

    &:hover {
      background-color: ${theme.palette.action.hover};
    }

    &:first-of-type {
      border-top: 0;
    }

    &:last-of-type {
      border-bottom: 0;
    }

    ${mediaMax(theme).xs`
      padding: 8px;
      text-align: center;
      text-transform: uppercase;

      background-color: ${selected ? theme.palette.action.hover : theme.palette.common.white};
    `};
  `};
`;

var FilterEnum = /*#__PURE__*/function (FilterEnum) {
  FilterEnum[FilterEnum["Today"] = 0] = "Today";
  FilterEnum[FilterEnum["LastWeek"] = 1] = "LastWeek";
  FilterEnum[FilterEnum["LastMonth"] = 2] = "LastMonth";
  FilterEnum[FilterEnum["LastQuarter"] = 3] = "LastQuarter";
  FilterEnum[FilterEnum["LastYear"] = 4] = "LastYear";
  FilterEnum[FilterEnum["FromTo"] = 5] = "FromTo";
  FilterEnum[FilterEnum["CustomDay"] = 6] = "CustomDay";
  return FilterEnum;
}(FilterEnum || {});
function DateRangePicker({
  label = 'Time period',
  name = 'dateRange',
  startDate = null,
  endDate = null,
  modifiers,
  dateFormat: rawDateFormat = 'yyyy-MM-dd',
  alignRight = false,
  disableRange = false,
  noRenderInputValueXsScreen = false,
  onChange,
  size,
  styles = {},
  allowFutureDates
}) {
  const dateFormat = convertFormatToDateFns(rawDateFormat);
  const theme = useTheme();
  const {
    screenIsAtMost
  } = useResponsive({
    xs: parseInt(theme.breakpoints[0], 10),
    sm: parseInt(theme.breakpoints[1], 10),
    md: parseInt(theme.breakpoints[2], 10),
    lg: parseInt(theme.breakpoints[3], 10)
  });
  const [isFilterOptionsVisible, setIsFilterOptionsVisible] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState(disableRange ? 6 : 0);
  const isXsScreen = screenIsAtMost('xs');
  const setSelectedFilterData = ({
    id
  }) => {
    setSelectedFilterId(id);
  };
  const getInputValue = () => {
    const inputValue = getInputValueLabel();
    if (noRenderInputValueXsScreen) {
      return inputValue ? ' ' : '';
    }
    if (!isFromToRangeSelected() && inputValue && !isCustomDateRangeSelected()) {
      return `${inputValue}`;
    }
    return inputValue;
  };
  const getInputValueLabel = () => {
    const isDatesDefined = startDate || endDate;
    if (isDatesDefined && isCustomDateRangeSelected() || isSameDay(startDate, endDate)) {
      return format(startDate, dateFormat) || '';
    }
    if (isDatesDefined) {
      const startDateLabel = startDate ? format(startDate, dateFormat) : '';
      const divider = startDate && endDate ? '-' : '';
      const endDateLabel = endDate ? format(endDate, dateFormat) : '';
      return `${startDateLabel} ${divider} ${endDateLabel}`;
    }
    return '';
  };
  const getFilter = filterId => {
    const filterOptions = getFilterOptions();
    return filterOptions.find(filter => filter.id === filterId);
  };
  const getFilterOptions = () => {
    return [{
      label: 'Today',
      onClick: filter => {
        handlePredefinedDateFilterClick({
          filter,
          start: new Date(),
          end: new Date()
        });
      },
      id: FilterEnum.Today,
      visible: true
    }, {
      label: 'Last 7 days',
      onClick: filter => {
        const today = new Date();
        handlePredefinedDateFilterClick({
          filter,
          start: subDays(today, 7),
          end: today
        });
      },
      id: FilterEnum.LastWeek,
      visible: true
    }, {
      label: 'Last month',
      onClick: filter => {
        const today = new Date();
        handlePredefinedDateFilterClick({
          filter,
          start: subMonths(today, 1),
          end: today
        });
      },
      id: FilterEnum.LastMonth,
      visible: true
    }, {
      label: 'Last quarter',
      onClick: filter => {
        const today = new Date();
        handlePredefinedDateFilterClick({
          filter,
          start: subMonths(today, 3),
          end: today
        });
      },
      id: FilterEnum.LastQuarter,
      visible: !isXsScreen
    }, {
      label: 'Last year',
      onClick: filter => {
        const today = new Date();
        handlePredefinedDateFilterClick({
          filter,
          start: subYears(today, 1),
          end: today
        });
      },
      id: FilterEnum.LastYear,
      visible: !isXsScreen
    }, {
      label: 'From - to',
      onClick: filter => {
        onChange({
          startDate: null,
          endDate: null
        });
        setSelectedFilterData(filter);
      },
      id: FilterEnum.FromTo,
      visible: true
    }, {
      label: 'Date',
      onClick: filter => {
        onChange({
          startDate: new Date(),
          endDate: new Date()
        });
        setSelectedFilterData(filter);
      },
      id: FilterEnum.CustomDay,
      visible: !isXsScreen
    }];
  };
  const handleOpenClick = () => {
    setIsFilterOptionsVisible(!isFilterOptionsVisible);
  };
  const handleDateChange = (selectedDate, modifiers, event) => {
    if (modifiers.isDisabled) {
      return;
    }
    const range = addToRange(selectedDate, {
      from: startDate,
      to: endDate
    });
    const resultRange = isXsScreen ? handleOnDateChangeForMobile(range, selectedDate) : handleOnDateChangeForDesktop(range, event.target);
    onChange({
      startDate: resultRange.from,
      endDate: resultRange.to
    });
    selectFromToCustomFilter();
  };
  const handleCustomDayChange = (date, modifiers) => {
    if (modifiers.isDisabled) {
      return;
    }
    onChange({
      startDate: date,
      endDate: date
    });
    handleCloseButtonClick(event);
  };
  const handleCloseButtonClick = event => {
    closeMenu();
  };
  const closeMenu = _ => {
    setIsFilterOptionsVisible(false);
  };
  const handleClickOutside = event => {
    if (!isXsScreen) {
      closeMenu();
    }
  };
  const handleOnDateChangeForMobile = (range, selectedDate) => {
    if (!range.from && !range.to) {
      return {
        from: selectedDate,
        to: selectedDate
      };
    }
    return range;
  };
  const selectFromToCustomFilter = () => {
    const fromToFilter = getFilter(FilterEnum.FromTo);
    setSelectedFilterData(fromToFilter);
  };
  const handleOnDateChangeForDesktop = (range, dayElement) => {
    const currentDayPicker = dayElement.closest('.rdp-month');
    const isFromDayPicker = currentDayPicker.nextSibling !== null;
    const newRange = _objectSpread2({}, range);
    const rangeIsSameDay = isSameDay(newRange.from, newRange.to);
    if (isFromDayPicker) {
      if (rangeIsSameDay) {
        newRange.to = null;
      }
    } else {
      if (!startDate && !endDate) {
        newRange.to = newRange.from;
        newRange.from = null;
      }
      if (rangeIsSameDay) {
        newRange.from = null;
      }
      if (isAfter(newRange.from, newRange.to)) {
        const {
          from: start,
          to: end
        } = newRange;
        newRange.from = end;
        newRange.to = start;
      }
    }
    return newRange;
  };
  const handleFilterClick = filter => {
    return () => filter.onClick(filter);
  };
  const handlePredefinedDateFilterClick = ({
    start,
    end,
    filter
  }) => {
    onChange({
      startDate: start,
      endDate: end
    });
    setSelectedFilterData(filter);
    if (!isXsScreen) {
      closeMenu(event);
    }
  };
  const isFromToRangeSelected = () => {
    return isFilterSelected(FilterEnum.FromTo);
  };
  const isCustomDateRangeSelected = () => {
    return isFilterSelected(FilterEnum.CustomDay);
  };
  const isFilterSelected = filterId => {
    const filter = getFilter(filterId);
    return filter.id === selectedFilterId;
  };
  const renderDatePicker = () => {
    const numberOfMonths = isXsScreen || isCustomDateRangeSelected() || disableRange ? 1 : 2;
    if (!modifiers) {
      modifiers = {
        start: startDate,
        end: endDate
      };
    }
    return /*#__PURE__*/createElement(DatePicker, {
      mode: "range",
      defaultMonth: endDate === null ? undefined : endDate,
      modifiers: modifiers,
      selected: {
        from: startDate,
        to: endDate
      },
      disabled: allowFutureDates ? undefined : {
        after: new Date()
      },
      numberOfMonths: numberOfMonths,
      onDayClick: isCustomDateRangeSelected() ? handleCustomDayChange : handleDateChange,
      showOutsideDays: true,
      fixedWeeks: true
    });
  };
  const renderFilterOptions = () => {
    const filterOptions = getFilterOptions().filter(item => item.visible);
    if (disableRange) {
      return null;
    }
    return /*#__PURE__*/createElement(FiltersUl, null, filterOptions.map(filter => /*#__PURE__*/createElement(FiltersLi, {
      key: filter.id,
      selected: selectedFilterId === filter.id,
      onClick: handleFilterClick(filter)
    }, filter.label)));
  };
  const renderFilterContainerDesktop = () => {
    return /*#__PURE__*/createElement(FiltersContainerDiv, _extends({
      alignRight: alignRight,
      testId: "filterContainer"
    }, styles === null || styles === void 0 ? void 0 : styles.filterContainer), renderFilterOptions(), renderDatePicker());
  };
  const renderFilterContainerMobile = () => {
    return /*#__PURE__*/createElement(Modal, {
      isOpen: isFilterOptionsVisible && isXsScreen,
      onClose: handleCloseButtonClick,
      actions: /*#__PURE__*/createElement(Button, {
        onClick: handleCloseButtonClick,
        block: true,
        variant: "primary"
      }, "Close")
    }, /*#__PURE__*/createElement(H4, {
      mb: theme.spacing.md
    }, label), renderFilterOptions(), renderDatePicker());
  };
  return /*#__PURE__*/createElement(ClickOutside, {
    onClickOutside: handleClickOutside,
    styles: {
      root: _objectSpread2({
        position: 'relative'
      }, styles.root)
    }
  }, /*#__PURE__*/createElement(TextInput, {
    size: size,
    name: name,
    label: label,
    value: getInputValue(),
    onFocus: handleOpenClick,
    autoComplete: "off",
    startInputAddon: /*#__PURE__*/createElement(InputAddon, {
      position: "start",
      isPointerEventsDisabled: true
    }, /*#__PURE__*/createElement(Icon, {
      glyph: icons.CALENDAR
    })),
    styles: {
      root: _objectSpread2({}, styles.inputRoot),
      input: _objectSpread2({
        cursor: 'pointer'
      }, styles.input),
      label: _objectSpread2({}, styles.inputLabel)
    }
  }), isFilterOptionsVisible && !isXsScreen && renderFilterContainerDesktop(), isFilterOptionsVisible && isXsScreen && renderFilterContainerMobile());
}

export default DateRangePicker;
